import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, Send, Trash2, Pencil, Copy, Check, X, ChevronLeft, FileText,
  Eye, Receipt, Mail,
} from "lucide-react";
import api from "../../lib/api";

const STATUS = {
  draft:    { l: "Brouillon", c: "var(--gai-grey-500)" },
  sent:     { l: "Envoyée",    c: "var(--gai-red)" },
  accepted: { l: "Acceptée",  c: "#0F9D58" },
  declined: { l: "Refusée",   c: "#D93025" },
  expired:  { l: "Expirée",   c: "var(--gai-grey-400)" },
};

const fmt = (n, c = "EUR") =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: c, maximumFractionDigits: 2 }).format(n || 0);

const dt = (s) => s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—";

function CopyBtn({ text, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
      className="gai-btn-ghost text-xs"
      data-testid={testid}
    >
      {done ? <Check size={12} /> : <Copy size={12} />} {done ? "Copié" : "Copier le lien"}
    </button>
  );
}

/* ---------- Editor ---------- */
function ProposalEditor({ initial, offers, onCancel, onSaved }) {
  const [form, setForm] = useState({
    client_email: initial?.client_email || "",
    client_name: initial?.client_name || "",
    client_company: initial?.client_company || "",
    title: initial?.title || "Proposition commerciale",
    intro_note: initial?.intro_note || "",
    items: initial?.items?.length ? initial.items : [],
    discount_pct: initial?.discount_pct ?? 0,
    currency: initial?.currency || "EUR",
    validity_days: initial?.validity_days ?? 30,
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);

  const addCustomLine = () => {
    setForm((f) => ({ ...f, items: [...f.items, { name: "", description: "", quantity: 1, unit_price: 0 }] }));
  };

  const addOffer = (offer) => {
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          offer_id: offer.id,
          name: offer.name,
          description: offer.description || "",
          quantity: 1,
          unit_price: offer.default_price,
        },
      ],
      currency: f.items.length ? f.currency : (offer.currency || f.currency),
    }));
    setPicker(false);
  };

  const updateLine = (i, patch) =>
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  const removeLine = (i) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const subtotal = useMemo(
    () => form.items.reduce((acc, it) => acc + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0),
    [form.items],
  );
  const discountAmount = useMemo(() => subtotal * ((parseFloat(form.discount_pct) || 0) / 100), [subtotal, form.discount_pct]);
  const total = subtotal - discountAmount;

  const save = async () => {
    if (!form.client_email || !form.items.length) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        discount_pct: parseFloat(form.discount_pct) || 0,
        validity_days: parseInt(form.validity_days, 10) || 30,
        items: form.items.map((it) => ({
          offer_id: it.offer_id || null,
          name: it.name,
          description: it.description || "",
          quantity: parseFloat(it.quantity) || 1,
          unit_price: parseFloat(it.unit_price) || 0,
        })),
      };
      let saved;
      if (initial?.id) {
        const r = await api.patch(`/admin/proposals/${initial.id}`, payload);
        saved = r.data;
      } else {
        const r = await api.post("/admin/proposals", payload);
        saved = r.data;
      }
      onSaved(saved);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="proposal-editor">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-1">{initial?.id ? `Modifier ${initial.number}` : "Nouvelle proposition"}</div>
          <h2 className="serif text-3xl text-[var(--gai-black)]">Composer un devis</h2>
        </div>
        <button onClick={onCancel} className="gai-btn-ghost text-xs">
          <ChevronLeft size={12} /> Retour à la liste
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT: client + items */}
        <div className="lg:col-span-2 space-y-5">
          <section className="gai-card gai-sharp p-5">
            <div className="overline mb-3">Client</div>
            <div className="grid md:grid-cols-2 gap-3">
              <input required type="email" className="gai-input" placeholder="Email du client"
                value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                data-testid="proposal-client-email" />
              <input className="gai-input" placeholder="Nom du contact"
                value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                data-testid="proposal-client-name" />
              <input className="gai-input md:col-span-2" placeholder="Société"
                value={form.client_company} onChange={(e) => setForm({ ...form, client_company: e.target.value })} />
            </div>
          </section>

          <section className="gai-card gai-sharp p-5">
            <div className="overline mb-3">Présentation</div>
            <input className="gai-input mb-3" placeholder="Titre de la proposition"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <textarea rows={3} className="gai-input resize-none"
              placeholder="Mot d'introduction (visible client)"
              value={form.intro_note} onChange={(e) => setForm({ ...form, intro_note: e.target.value })} />
          </section>

          <section className="gai-card gai-sharp p-5" data-testid="proposal-items">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Lignes ({form.items.length})</div>
              <div className="flex gap-2">
                <button onClick={() => setPicker(true)} className="gai-btn-outline text-xs" data-testid="proposal-add-from-catalog">
                  <Receipt size={12} /> Ajouter du catalogue
                </button>
                <button onClick={addCustomLine} className="gai-btn-ghost text-xs" data-testid="proposal-add-custom">
                  <Plus size={12} /> Ligne libre
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {form.items.length === 0 && (
                <div className="text-sm text-[var(--gai-grey-500)] italic p-4 border border-dashed" style={{ borderColor: "var(--gai-grey-300)" }}>
                  Aucune ligne. Ajoutez une offre du catalogue ou une ligne libre.
                </div>
              )}
              {form.items.map((it, i) => (
                <div key={i} className="border p-3 space-y-2" style={{ borderColor: "var(--gai-grey-200)" }} data-testid={`proposal-item-${i}`}>
                  <div className="flex gap-2 items-start">
                    <input className="gai-input flex-1" placeholder="Désignation"
                      value={it.name} onChange={(e) => updateLine(i, { name: e.target.value })} />
                    <button onClick={() => removeLine(i)} className="gai-btn-ghost text-red-300 p-2" aria-label="Supprimer">
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <input className="gai-input text-xs" placeholder="Description (optionnelle)"
                    value={it.description || ""} onChange={(e) => updateLine(i, { description: e.target.value })} />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <div className="text-[0.6rem] uppercase tracking-[0.18em] mb-1 text-[var(--gai-grey-500)]">Quantité</div>
                      <input type="number" min="0" step="0.5" className="gai-input"
                        value={it.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} />
                    </div>
                    <div>
                      <div className="text-[0.6rem] uppercase tracking-[0.18em] mb-1 text-[var(--gai-grey-500)]">PU</div>
                      <input type="number" min="0" step="50" className="gai-input"
                        value={it.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} />
                    </div>
                    <div>
                      <div className="text-[0.6rem] uppercase tracking-[0.18em] mb-1 text-[var(--gai-grey-500)]">Total ligne</div>
                      <div className="serif text-lg text-[var(--gai-black)] py-2">
                        {fmt((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), form.currency)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="gai-card gai-sharp p-5">
            <div className="overline mb-3">Notes internes</div>
            <textarea rows={3} className="gai-input resize-none"
              placeholder="Notes (non visibles par le client)"
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </section>
        </div>

        {/* RIGHT: totals + meta */}
        <aside className="space-y-5">
          <div className="gai-card gai-sharp p-5 sticky top-4" data-testid="proposal-summary">
            <div className="overline mb-4">Récapitulatif</div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--gai-grey-600)]">Sous-total</span>
                <span className="text-[var(--gai-black)]">{fmt(subtotal, form.currency)}</span>
              </div>

              <div className="flex justify-between items-center">
                <label className="text-[var(--gai-grey-600)] flex items-center gap-2">
                  Remise
                  <input type="number" min="0" max="100" step="1" className="gai-input w-16 px-2 py-1 text-xs"
                    value={form.discount_pct}
                    onChange={(e) => setForm({ ...form, discount_pct: e.target.value })}
                    data-testid="proposal-discount" />
                  <span className="text-xs">%</span>
                </label>
                <span className="text-[var(--gai-black)]">- {fmt(discountAmount, form.currency)}</span>
              </div>

              <div className="h-px my-3" style={{ background: "var(--gai-grey-200)" }} />

              <div className="flex justify-between items-end">
                <span className="overline">Total</span>
                <span className="serif text-3xl gai-gradient-gold leading-none" data-testid="proposal-total">
                  {fmt(total, form.currency)}
                </span>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t space-y-3" style={{ borderColor: "var(--gai-grey-200)" }}>
              <div>
                <div className="overline mb-1">Devise</div>
                <select className="gai-input" value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                  {["EUR", "USD", "XOF", "XAF", "CDF", "MAD", "GBP"].map((c) =>
                    <option key={c} value={c} style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>{c}</option>)}
                </select>
              </div>
              <div>
                <div className="overline mb-1">Validité (jours)</div>
                <input type="number" min="1" className="gai-input"
                  value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} />
              </div>
            </div>

            <div className="mt-5 pt-4 border-t flex flex-col gap-2" style={{ borderColor: "var(--gai-grey-200)" }}>
              <button onClick={save} disabled={busy || !form.client_email || !form.items.length}
                className="gai-btn-gold text-xs disabled:opacity-50"
                data-testid="proposal-save">
                {busy ? "Enregistrement…" : (initial?.id ? "Enregistrer les modifications" : "Créer le brouillon")}
              </button>
              <button onClick={onCancel} className="gai-btn-ghost text-xs">Annuler</button>
            </div>
          </div>
        </aside>
      </div>

      {/* Picker */}
      {picker && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
          <div className="w-full max-w-3xl gai-card gai-sharp p-6 max-h-[85vh] overflow-y-auto relative" data-testid="catalog-picker">
            <button onClick={() => setPicker(false)} className="absolute top-4 right-4 text-[var(--gai-grey-600)] hover:text-[var(--gai-gold)]">
              <X size={18} />
            </button>
            <div className="overline mb-1">Catalogue</div>
            <h3 className="serif text-2xl text-[var(--gai-black)] mb-5">Choisir une offre</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {offers.length === 0 && (
                <div className="text-sm text-[var(--gai-grey-500)] md:col-span-2">Aucune offre disponible. Créez d'abord une offre dans le catalogue.</div>
              )}
              {offers.filter((o) => o.active).map((o) => (
                <button key={o.id} onClick={() => addOffer(o)}
                  className="text-left p-4 border hover:border-[var(--gai-gold)] transition group"
                  style={{ borderColor: "var(--gai-grey-200)" }}
                  data-testid={`pick-offer-${o.id}`}>
                  <div className="overline text-[var(--gai-gold)]">{o.category.replace("_", " ")}</div>
                  <div className="serif text-base text-[var(--gai-black)] mt-1 group-hover:gai-gradient-gold">{o.name}</div>
                  <div className="text-xs text-[var(--gai-grey-500)] mt-1 line-clamp-2">{o.description}</div>
                  <div className="mt-3 flex justify-between items-end">
                    <span className="serif text-lg gai-gradient-gold">{fmt(o.default_price, o.currency)}</span>
                    <span className="text-[0.6rem] uppercase tracking-[0.18em] text-[var(--gai-grey-500)]">/ {o.unit}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Sent confirmation modal ---------- */
function SendConfirm({ proposal, onClose }) {
  if (!proposal) return null;
  const link = `${window.location.origin}/proposition/${proposal.token}`;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
      <div className="w-full max-w-xl gai-card gai-sharp p-7 relative" data-testid="proposal-sent-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--gai-grey-600)] hover:text-[var(--gai-gold)]">
          <X size={18} />
        </button>
        <div className="overline" style={{ color: "var(--gai-gold)" }}>Proposition envoyée</div>
        <h3 className="serif text-2xl text-[var(--gai-black)] mb-2 mt-1">{proposal.number}</h3>
        <p className="text-sm text-[var(--gai-grey-700)] mb-4">
          Le devis est maintenant disponible à cette adresse. Copiez-la et envoyez-la à <strong>{proposal.client_email}</strong> par email.
        </p>
        <div className="border p-3 flex items-center gap-3 flex-wrap" style={{ borderColor: "var(--gai-gold-dim)" }}>
          <code className="text-xs flex-1 break-all text-[var(--gai-grey-800)]">{link}</code>
          <CopyBtn text={link} testid="proposal-sent-copy" />
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <a href={`mailto:${proposal.client_email}?subject=${encodeURIComponent(proposal.number + " — " + proposal.title)}&body=${encodeURIComponent("Bonjour,\n\nVeuillez trouver notre proposition commerciale au lien suivant :\n\n" + link + "\n\nCordialement,\nGLOBAL AFRIQUE INTERNATIONAL")}`}
            className="gai-btn-outline text-xs">
            <Mail size={12} /> Ouvrir l'email
          </a>
          <button onClick={onClose} className="gai-btn-gold text-xs" data-testid="proposal-sent-done">Terminé</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function Proposals() {
  const [proposals, setProposals] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | proposal
  const [sentModal, setSentModal] = useState(null);

  const refresh = async () => {
    setLoading(true);
    const [p, o] = await Promise.all([
      api.get("/admin/proposals").then((r) => r.data).catch(() => []),
      api.get("/admin/offers").then((r) => r.data).catch(() => []),
    ]);
    setProposals(p);
    setOffers(o);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const counts = useMemo(() => {
    const m = { all: proposals.length, draft: 0, sent: 0, accepted: 0, declined: 0 };
    proposals.forEach((p) => { if (m[p.status] !== undefined) m[p.status] += 1; });
    return m;
  }, [proposals]);

  const filtered = proposals.filter((p) => statusFilter === "all" || p.status === statusFilter);

  const send = async (p) => {
    if (!window.confirm(`Envoyer la proposition ${p.number} ?\n\nUn lien public sera généré pour le client.`)) return;
    const { data } = await api.post(`/admin/proposals/${p.id}/send`);
    setSentModal(data);
    refresh();
  };
  const remove = async (p) => {
    if (!window.confirm(`Supprimer ${p.number} ?`)) return;
    await api.delete(`/admin/proposals/${p.id}`);
    refresh();
  };

  if (editing) {
    return (
      <ProposalEditor
        initial={editing === "new" ? null : editing}
        offers={offers}
        onCancel={() => setEditing(null)}
        onSaved={(saved) => { setEditing(null); refresh(); if (saved && saved.status === "draft") setEditing(null); }}
      />
    );
  }

  return (
    <div data-testid="page-admin-proposals">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="overline mb-2">Pipeline commercial</div>
          <h1 className="serif text-4xl md:text-5xl text-[var(--gai-black)] leading-none">Devis & Propositions</h1>
          <p className="mt-2 text-sm text-[var(--gai-grey-500)] max-w-2xl">
            Composez vos propositions à partir du catalogue, envoyez-les par lien sécurisé et suivez chaque décision client.
          </p>
        </div>
        <button onClick={() => setEditing("new")} className="gai-btn-gold text-xs" data-testid="btn-new-proposal">
          <Plus size={12} /> Nouvelle proposition
        </button>
      </div>

      {/* Pipeline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-7" data-testid="proposals-kpis">
        {[
          { k: "all", l: "Total", v: counts.all, c: "var(--gai-gold)" },
          { k: "draft", l: "Brouillons", v: counts.draft, c: STATUS.draft.c },
          { k: "sent", l: "Envoyées", v: counts.sent, c: STATUS.sent.c },
          { k: "accepted", l: "Acceptées", v: counts.accepted, c: STATUS.accepted.c },
          { k: "declined", l: "Refusées", v: counts.declined, c: STATUS.declined.c },
        ].map((t) => (
          <button key={t.k}
            onClick={() => setStatusFilter(t.k)}
            className={`gai-card gai-sharp p-4 text-left transition ${statusFilter === t.k ? "ring-1" : "opacity-80"}`}
            style={{ outline: statusFilter === t.k ? `1px solid ${t.c}` : "none" }}
            data-testid={`proposals-filter-${t.k}`}
          >
            <div className="serif text-2xl" style={{ color: t.c }}>{t.v}</div>
            <div className="mt-1 text-[0.66rem] tracking-[0.18em] uppercase text-[var(--gai-grey-500)]">{t.l}</div>
          </button>
        ))}
      </div>

      {/* List */}
      <div className="gai-card gai-sharp overflow-hidden" data-testid="proposals-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.66rem] tracking-[0.2em] uppercase text-[var(--gai-grey-500)]" style={{ background: "var(--gai-grey-50)" }}>
              <th className="py-3 px-5">N° / Client</th>
              <th className="py-3 px-5">Titre</th>
              <th className="py-3 px-5 text-right">Total</th>
              <th className="py-3 px-5">Statut</th>
              <th className="py-3 px-5">Envoyée le</th>
              <th className="py-3 px-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-8 text-center text-[var(--gai-grey-500)]">Chargement…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-[var(--gai-grey-500)]">
                <FileText className="mx-auto mb-3 opacity-50" size={28} />
                Aucune proposition pour ce filtre.
              </td></tr>
            )}
            {filtered.map((p) => {
              const s = STATUS[p.status] || STATUS.draft;
              const link = `${window.location.origin}/proposition/${p.token}`;
              return (
                <tr key={p.id} className="border-t" style={{ borderColor: "var(--gai-grey-200)" }} data-testid={`proposal-row-${p.id}`}>
                  <td className="py-3 px-5">
                    <div className="font-medium text-[var(--gai-gold)]">{p.number}</div>
                    <div className="text-xs text-[var(--gai-grey-700)] mt-0.5">{p.client_name || p.client_email}</div>
                    {p.client_company && <div className="text-[0.65rem] text-[var(--gai-grey-500)]">{p.client_company}</div>}
                  </td>
                  <td className="py-3 px-5 text-[var(--gai-black)]">{p.title}</td>
                  <td className="py-3 px-5 text-right serif text-base text-[var(--gai-black)]">{fmt(p.total, p.currency)}</td>
                  <td className="py-3 px-5">
                    <span className="text-[0.66rem] tracking-[0.18em] uppercase" style={{ color: s.c }}>● {s.l}</span>
                    {p.decision_note && (
                      <div className="text-[0.65rem] text-[var(--gai-grey-500)] mt-0.5 italic line-clamp-1">« {p.decision_note} »</div>
                    )}
                  </td>
                  <td className="py-3 px-5 text-xs text-[var(--gai-grey-600)]">{dt(p.sent_at)}</td>
                  <td className="py-3 px-5">
                    <div className="flex justify-end gap-1 flex-wrap">
                      {p.status === "draft" && (
                        <>
                          <button onClick={() => setEditing(p)} className="gai-btn-ghost text-xs" data-testid={`edit-${p.id}`}>
                            <Pencil size={12} /> Modifier
                          </button>
                          <button onClick={() => send(p)} className="gai-btn-gold text-xs" data-testid={`send-${p.id}`}>
                            <Send size={12} /> Envoyer
                          </button>
                        </>
                      )}
                      {p.status === "sent" && (
                        <>
                          <a href={link} target="_blank" rel="noreferrer" className="gai-btn-ghost text-xs" data-testid={`view-${p.id}`}>
                            <Eye size={12} /> Aperçu
                          </a>
                          <CopyBtn text={link} testid={`copy-${p.id}`} />
                          <button onClick={() => setEditing(p)} className="gai-btn-ghost text-xs">
                            <Pencil size={12} />
                          </button>
                        </>
                      )}
                      {(p.status === "accepted" || p.status === "declined") && (
                        <a href={link} target="_blank" rel="noreferrer" className="gai-btn-ghost text-xs" data-testid={`view-decided-${p.id}`}>
                          <Eye size={12} /> Voir
                        </a>
                      )}
                      <button onClick={() => remove(p)} className="gai-btn-ghost p-2 text-red-300 hover:text-red-200" aria-label="Supprimer">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sentModal && <SendConfirm proposal={sentModal} onClose={() => setSentModal(null)} />}
    </div>
  );
}
